import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import {
	useConfiguration,
	useFormBuilder,
	useLocale,
} from '@gov-nx/core/service';
import {
	getRadioDefinition,
	useInputDefinition,
	useOnChange,
} from '@gov-nx/utils/common';
import { FormInput, FormRadio } from '@gov-nx/ui/web';
import { is } from '@gov-nx/core/types';
import type { Nullable } from '@gov-nx/core/types';
import { useAutorizaceContext } from '../Context';
import { AuthorizationType } from '../types';
import type { AuthorizationForm, AuthorizationFormFields } from './types';
import { AUTHORIZATION_METHOD_COPY, RESEND_COUNTDOWN_SECONDS } from './methods';
import { MethodPanel } from './MethodPanel';
import { buildIdentityUdajeUrl, getRedirectUrl } from './utils';
import './styles.css';

interface AuthorizationMethodsProps {
	code: string;
	allowedAuthorizationTypes: AuthorizationType[];
}

const OPTION_ORDER: AuthorizationType[] = [
	AuthorizationType.SMS,
	AuthorizationType.FCM,
	AuthorizationType.AUTENTIZACE,
];

/**
 * Per-method resend cooldown. Lifted out of MethodPanel so it survives the
 * selected panel unmounting on a method switch — keeping it in the panel let a
 * user reset the 15s throttle just by toggling methods. Keyed by authorization
 * method: the cooldown (re)starts for the method that initiated a send only when
 * that send actually succeeds (`sendSucceeded`, i.e. sendAuthCodeQuery.isSuccess,
 * false → true). Gating on the raw loading flag instead would arm the throttle
 * after a failed (re)send too, blocking the user from retrying even though no new
 * code was delivered. Every active method ticks down on a single shared timer.
 */
const useResendCountdowns = (
	activeSendType: Nullable<AuthorizationType>,
	sendSucceeded: boolean
): Partial<Record<AuthorizationType, number>> => {
	const [secondsByMethod, setSecondsByMethod] = useState<
		Partial<Record<AuthorizationType, number>>
	>({});

	useOnChange(sendSucceeded, (succeeded, wasSucceeded) => {
		if (!wasSucceeded && succeeded && is(activeSendType)) {
			setSecondsByMethod((prev) => ({
				...prev,
				[activeSendType]: RESEND_COUNTDOWN_SECONDS,
			}));
		}
	});

	useEffect(() => {
		const hasActive = Object.values(secondsByMethod).some((s) => s > 0);
		if (!hasActive) {
			return;
		}
		const id = setTimeout(() => {
			setSecondsByMethod((prev) => {
				const next: Partial<Record<AuthorizationType, number>> = {};
				for (const method of Object.keys(prev) as AuthorizationType[]) {
					next[method] = Math.max(0, (prev[method] ?? 0) - 1);
				}
				return next;
			});
		}, 1000);
		return () => clearTimeout(id);
	}, [secondsByMethod]);

	return secondsByMethod;
};

/**
 * The whole authorization flow on a single screen (replaces the former
 * Step1 + Step2 wizard). A radio group lists the available methods; selecting
 * one expands an inline {@link MethodPanel} directly under the option with its
 * subtitle, action button and — after a code is sent — the code input.
 */
export const AuthorizationMethods = ({
	code,
	allowedAuthorizationTypes,
}: AuthorizationMethodsProps) => {
	const { autorizaceNiaViaLoginUrl, identitaObcanaUrl } = useConfiguration();
	const authContext = useAutorizaceContext();
	const { t } = useLocale(code);

	const submitting = !!authContext?.confirmAuthCode.isLoading;

	const codeInputDefinition = useInputDefinition({
		name: 'oneTimePassword',
		label: t('code-input.label'),
		placeholder: '',
		isRequired: true,
		isDisabled: submitting,
	});

	const radioDefinition = getRadioDefinition<AuthorizationType>({
		name: 'authorizationType',
		label: t('dialog.methods-label'),
		defaultValue: null,
		options: OPTION_ORDER.map((type) => ({
			name: type,
			label: t(`authorization-type.${type.toLowerCase()}.label`),
		})).filter((option) => allowedAuthorizationTypes.includes(option.name)),
	});

	const form = useFormBuilder<AuthorizationForm, AuthorizationFormFields>([
		radioDefinition,
		codeInputDefinition,
	]);

	const selected = useWatch({
		control: form.formMethods.control,
		name: 'authorizationType',
	}) as Nullable<AuthorizationType>;

	// sendAuthCodeQuery is shared across methods, so its loading/success flags
	// are only meaningful for the method that actually initiated the send.
	// variables holds the last mutate() args (see Context.onNextStep), whose
	// body.kanal is the initiating channel. Guarding on it prevents a late
	// success from a previously selected method (e.g. SMS) from rendering the
	// newly selected method's panel (e.g. FCM) as if its code had been sent —
	// a real OTP-channel mix-up.
	const activeSendType = authContext?.sendAuthCodeQuery.variables?.body
		?.kanal as Nullable<AuthorizationType>;
	const sending =
		!!authContext?.sendAuthCodeQuery.isLoading && activeSendType === selected;

	// Latch, per method, whether a code was ever successfully sent. Deriving the
	// confirm UI straight from sendAuthCodeQuery.isSuccess would collapse it the
	// moment a resend re-enters loading (isSuccess flips back to false) and keep it
	// collapsed after a resend failure — stranding the user without the OTP input
	// even though the already-delivered code is still valid. The latch keeps the
	// confirm UI up once a send has succeeded, regardless of later resend attempts.
	const [sentMethods, setSentMethods] = useState<Set<AuthorizationType>>(
		() => new Set()
	);
	useEffect(() => {
		if (authContext?.sendAuthCodeQuery.isSuccess && is(activeSendType)) {
			setSentMethods((prev) => {
				if (prev.has(activeSendType)) {
					return prev;
				}
				const next = new Set(prev);
				next.add(activeSendType);
				return next;
			});
		}
	}, [authContext?.sendAuthCodeQuery.isSuccess, activeSendType]);

	const codeSent = is(selected) && sentMethods.has(selected);

	// Cooldown is keyed on the initiating channel (activeSendType) and armed on a
	// successful send only. react-query resets isSuccess to false whenever a resend
	// re-enters loading (the same behaviour the sentMethods latch above depends on),
	// so isSuccess flips false → true once per successful (re)send — including for a
	// method the user has already switched away from, which still throttles the
	// channel it belongs to. A failed resend leaves isSuccess false, so it never
	// arms the cooldown.
	const resendCountdowns = useResendCountdowns(
		activeSendType,
		!!authContext?.sendAuthCodeQuery.isSuccess
	);
	const resendSecondsLeft = is(selected)
		? (resendCountdowns[selected] ?? 0)
		: 0;

	// Clear the entered code when switching methods. The per-method sent-code latch
	// above is intentionally preserved, so returning to a method that already
	// received a code still shows its confirm UI rather than the send button.
	useEffect(() => {
		form.formMethods.resetField('oneTimePassword');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selected]);

	if (!authContext) {
		return null;
	}

	const identityUrl = buildIdentityUdajeUrl(identitaObcanaUrl);

	const onAction = (type: AuthorizationType) => {
		if (type === AuthorizationType.AUTENTIZACE) {
			window.location.href = getRedirectUrl(
				autorizaceNiaViaLoginUrl,
				authContext.ukonId
			);
			return;
		}
		authContext.onNextStep(type);
	};

	// The shared OTP input takes its placeholder from the selected method.
	const placeholderKey = selected
		? AUTHORIZATION_METHOD_COPY[selected].placeholderKey
		: undefined;
	if (placeholderKey) {
		form.fields.oneTimePassword.field.placeholder = t(placeholderKey);
	}

	// Freeze method selection while an OTP confirmation is in flight, mirroring
	// the old wizard (its method-selection step was `disabled` during
	// confirmAuthCode.isLoading). Otherwise the user could switch methods —
	// triggering another send — while the previous method's confirm is pending.
	form.fields.authorizationType.field.isDisabled = submitting;

	// Render the inline panel only under the currently selected option.
	form.fields.authorizationType.field.options =
		form.fields.authorizationType.field.options.map((option) => {
			if (option.value !== selected) {
				return option;
			}
			const type = option.value as AuthorizationType;
			return {
				...option,
				children: (
					<MethodPanel
						code={code}
						type={type}
						identityUrl={identityUrl}
						codeSent={codeSent}
						sending={sending}
						resendSecondsLeft={resendSecondsLeft}
						submitting={submitting}
						codeInput={
							// The design shows no visible label above the code input —
							// the placeholder describes it. Keep the label text in the
							// DOM but visually hidden so screen readers still announce it.
							<FormInput
								{...form.fields.oneTimePassword}
								label={{
									...form.fields.oneTimePassword.label,
									className: 'sr-only',
								}}
							/>
						}
						onAction={() => onAction(type)}
						onResend={() => onAction(type)}
					/>
				),
			};
		});

	return (
		<GovFormWeb
			formDefinition={form}
			onSubmit={form.formMethods.handleSubmit(authContext.onConfirmSubmit)}>
			{/*
			 * .autorizace-method-options (see styles.css) bumps each radio option
			 * label from the size-m default (body-m) to body-l. A plain CSS class is
			 * used instead of a Tailwind arbitrary-variant utility because the latter
			 * was not reliably emitted in the app build; the dedicated rule is always
			 * present and overrides the unlayered DS label rule.
			 */}
			<div className="autorizace-method-options">
				{/*
				 * size="xl" renders the legend at --font-size-body-xl (20px).
				 * mb-(--spacing-s)! (8px) spaces the legend from the options —
				 * important because `:is(gov-form-control) * { margin-bottom: 0 }`
				 * (unlayered) zeroes the legend's margin otherwise.
				 * gap="m" (--spacing-m = 16px) spaces out the authorization options.
				 */}
				<FormRadio
					{...form.fields.authorizationType}
					label={{
						...form.fields.authorizationType.label,
						size: 'xl',
						className: 'mb-(--spacing-s)!',
					}}
					group={{ gap: 'm' }}
				/>
			</div>
		</GovFormWeb>
	);
};
